import type { Card, PlayedCard, SeatIndex, Suit, TrickState } from "@meksiko/shared-types";
import { GameEngineError } from "./errors.js";

/**
 * GAME_SPEC.md §7: follow the led suit if possible; if not, you must cut with
 * trump if you hold one; only if you hold neither may you play anything.
 * When trump is null (no trump suit chosen yet, or the "mexico" special deal
 * which is played with no trump at all) the cutting rule never applies.
 */
export function legalPlays(hand: Card[], trick: TrickState, trump: Suit | null): Card[] {
  if (trick.cards.length === 0) {
    return hand;
  }
  const ledSuit = trick.cards[0]!.card.suit;
  const followers = hand.filter((c) => c.suit === ledSuit);
  if (followers.length > 0) return followers;

  if (trump !== null) {
    const trumps = hand.filter((c) => c.suit === trump);
    if (trumps.length > 0) return trumps;
  }

  return hand;
}

export function applyPlayCard(
  hand: Card[],
  trick: TrickState,
  trump: Suit | null,
  seat: SeatIndex,
  card: Card,
): { hand: Card[]; trick: TrickState } {
  if (trick.turn !== seat) {
    throw new GameEngineError(`Not seat ${seat}'s turn to play`, "NOT_YOUR_TURN");
  }
  const owned = hand.find((c) => c.suit === card.suit && c.rank === card.rank);
  if (!owned) {
    throw new GameEngineError("Card not in hand", "CARD_NOT_IN_HAND");
  }
  const legal = legalPlays(hand, trick, trump);
  if (!legal.some((c) => c.suit === card.suit && c.rank === card.rank)) {
    throw new GameEngineError(
      "Illegal card: must follow suit, or cut with trump if unable to follow",
      "MUST_FOLLOW_SUIT",
    );
  }

  const newHand = hand.filter((c) => !(c.suit === card.suit && c.rank === card.rank));
  const played: PlayedCard = { seat, card };
  const cards = [...trick.cards, played];
  const turn = ((seat + 1) % 3) as SeatIndex;

  return { hand: newHand, trick: { ...trick, cards, turn } };
}

/** Highest trump wins the trick; if no trump was played, highest card of the led suit wins. */
export function resolveTrickWinner(trick: TrickState, trump: Suit | null): SeatIndex {
  if (trick.cards.length !== 3) {
    throw new GameEngineError("Cannot resolve an incomplete trick", "TRICK_INCOMPLETE");
  }
  const ledSuit = trick.cards[0]!.card.suit;

  if (trump !== null) {
    const trumpPlays = trick.cards.filter((pc) => pc.card.suit === trump);
    if (trumpPlays.length > 0) {
      return trumpPlays.reduce((best, pc) => (pc.card.rank > best.card.rank ? pc : best)).seat;
    }
  }

  const ledSuitPlays = trick.cards.filter((pc) => pc.card.suit === ledSuit);
  return ledSuitPlays.reduce((best, pc) => (pc.card.rank > best.card.rank ? pc : best)).seat;
}
